import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import {
	POLLING_CONFIG,
	PollingConfig,
	QUERY_KEYS,
} from '../../shared/constants/polling.constants';
import { ProductItem, ProductPayload } from '../types/product.types';
import {
	hasDataChanged,
	hasPayloadDataChanged,
} from '../utils/productComparison';

export const useProductPolling = () => {
	const queryClient = useQueryClient();

	const getCurrentTableData = useCallback((): ProductItem[] => {
		const queryData =
			queryClient.getQueryData(QUERY_KEYS.PRODUTO_LIST) ||
			queryClient.getQueryData([...QUERY_KEYS.PRODUTO_LIST, 0, 100]) ||
			queryClient.getQueryData([
				...QUERY_KEYS.PRODUTO_LIST,
				0,
				100,
				[],
			]) ||
			queryClient.getQueryData([
				...QUERY_KEYS.PRODUTO_LIST,
				0,
				100,
				undefined,
			]);

		return (queryData as any)?.data || [];
	}, [queryClient]);

	const startDataChangeDetection = useCallback(
		(
			payload: { arquivo: ProductPayload[] } | null,
			previousData: ProductItem[] | null,
			showSuccessToast: () => void,
			showProcessingCompleteToast: () => void,
			setIsProcessing: (value: boolean) => void,
			pollingConfig: PollingConfig = POLLING_CONFIG
		) => {
			const maxAttempts = pollingConfig.MAX_ATTEMPTS;
			let attempts = 0;
			let stableVerifications = 0;
			let lastDataSnapshot: ProductItem[] | null = null;
			let isCompleted = false;
			let hasDetectedChanges = false;
			let toastShown = false;
			const processingToastKey =
				'product-upload-processing-complete-toast';
			const successToastKey = 'product-upload-success-toast';

			setIsProcessing(true);
			localStorage.setItem(
				'product-upload-processing',
				Date.now().toString()
			);
			window.dispatchEvent(
				new Event('product-upload-processing-changed')
			);

			const checkForChanges = async () => {
				if (isCompleted) {
					return;
				}

				if (attempts >= maxAttempts) {
					isCompleted = true;
					setIsProcessing(false);
					localStorage.removeItem('product-upload-processing');
					localStorage.removeItem('product-upload-polling-started');
					localStorage.removeItem('product-upload-last-block-tag');
					localStorage.removeItem('product-upload-file-lines');
					window.dispatchEvent(
						new Event('product-upload-processing-changed')
					);
					if (!hasDetectedChanges && !toastShown) {
						const existingToast =
							localStorage.getItem(processingToastKey);
						if (!existingToast) {
							toastShown = true;
							localStorage.setItem(processingToastKey, 'true');
							showProcessingCompleteToast();
							setTimeout(() => {
								localStorage.removeItem(processingToastKey);
							}, 1000);
						}
					}
					return;
				}

				await queryClient.refetchQueries({
					queryKey: QUERY_KEYS.PRODUTO_LIST,
				});

				const currentData = getCurrentTableData();

				const isUpdated = payload
					? hasPayloadDataChanged(payload, currentData)
					: hasDataChanged(previousData ?? [], currentData);

				if (isUpdated) {
					hasDetectedChanges = true;
					const hasNewChanges = lastDataSnapshot
						? hasDataChanged(lastDataSnapshot, currentData)
						: true;

					if (hasNewChanges) {
						stableVerifications = 0;
						lastDataSnapshot = [...currentData];
					} else {
						stableVerifications++;
					}

					if (
						stableVerifications >=
						pollingConfig.STABLE_VERIFICATIONS_REQUIRED
					) {
						isCompleted = true;
						setIsProcessing(false);
						localStorage.removeItem('product-upload-processing');
						localStorage.removeItem(
							'product-upload-polling-started'
						);
						localStorage.removeItem(
							'product-upload-last-block-tag'
						);
						localStorage.removeItem('product-upload-file-lines');
						window.dispatchEvent(
							new Event('product-upload-processing-changed')
						);
						if (!toastShown) {
							const existingToast =
								localStorage.getItem(successToastKey);
							if (!existingToast) {
								toastShown = true;
								localStorage.setItem(successToastKey, 'true');
								showSuccessToast();
								setTimeout(() => {
									localStorage.removeItem(successToastKey);
								}, 1000);
							}
						}
						return;
					}
					setTimeout(checkForChanges, pollingConfig.INTERVAL_MS);
				} else {
					stableVerifications = 0;
					lastDataSnapshot = null;
					attempts++;
					setTimeout(checkForChanges, pollingConfig.INTERVAL_MS);
				}
			};

			setTimeout(checkForChanges, pollingConfig.INITIAL_DELAY_MS);
		},
		[queryClient, getCurrentTableData]
	);

	return {
		getCurrentTableData,
		startDataChangeDetection,
	};
};
